import React, { useState, useEffect, useRef } from 'react';
import { ID_PATTERN_TOKENS, CHAR_CLASSES, PLACEHOLDER_SAMPLES, PATTERN_KEYWORDS, REGEX_FLAGS } from 'utils/constants';

/**
 * Generate a sample placeholder for any tag rule dynamically.
 * Supports known patterns and falls back to generic behavior for unknown tags.
 * @param {string} rule - The rule name (e.g., "LETTERS", "DIGITS", "INCR")
 * @param {number|string} length - For most rules: length (number). For INCR: starting value string (e.g., "01", "001")
 */
function getSamplePlaceholder(rule, length) {
  const normalizedRule = String(rule || '').toUpperCase().trim();
  
  // Handle INCR first since it has special parameter handling
  if (normalizedRule === ID_PATTERN_TOKENS.INCR || normalizedRule === ID_PATTERN_TOKENS.INCREMENT || normalizedRule === ID_PATTERN_TOKENS.INCREMENTAL) {
    // For INCR, length parameter is the starting value string (e.g., "01", "001")
    if (typeof length === 'string') {
      return length;
    }
    if (typeof length === 'number' && length > 0) {
      return String(length);
    }
    return PLACEHOLDER_SAMPLES.INCR_DEFAULT;
  }
  
  // Use default length of 4 if not provided, so we can show a sample
  const displayLength = typeof length === 'number' ? length : (length || 4);
  
  // Use switch for primary rule matching
  switch (normalizedRule) {
    case ID_PATTERN_TOKENS.LETTER:
    case ID_PATTERN_TOKENS.LETTERS:
    case ID_PATTERN_TOKENS.LETTERS_UPPERCASE:
      // Letters (Uppercase) → only A-Z
      return PLACEHOLDER_SAMPLES.LETTERS_UPPER.slice(0, displayLength);

    case ID_PATTERN_TOKENS.LETTERS_LOWERCASE:
    case ID_PATTERN_TOKENS.LETTER_LOWER:
      // Letters (Lowercase) → only a-z
      return PLACEHOLDER_SAMPLES.LETTERS_LOWER.slice(0, displayLength);

    case ID_PATTERN_TOKENS.LETTERS_MIXED:
    case ID_PATTERN_TOKENS.LETTER_MIXED:
      // Letters (Mixed Case) → random mix
      return 'Ab'.repeat(Math.ceil(displayLength / 2)).slice(0, displayLength);

    case ID_PATTERN_TOKENS.DIGITS:
    case ID_PATTERN_TOKENS.DIGIT:
      // Digits (0-9)
      return PLACEHOLDER_SAMPLES.DIGITS.repeat(Math.ceil(displayLength / PLACEHOLDER_SAMPLES.DIGITS.length)).slice(0, displayLength);

    case ID_PATTERN_TOKENS.ALPHANUMERIC_UPPERCASE:
    case ID_PATTERN_TOKENS.ALNUM:
    case ID_PATTERN_TOKENS.ALNUM_UPPERCASE:
      // Alphanumerics (Uppercase) → [A-Z0-9]
      return PLACEHOLDER_SAMPLES.ALNUM_UPPER_PATTERN.repeat(Math.ceil(displayLength / PLACEHOLDER_SAMPLES.ALNUM_UPPER_PATTERN.length)).slice(0, displayLength);

    case ID_PATTERN_TOKENS.ALPHANUMERIC_LOWERCASE:
    case ID_PATTERN_TOKENS.ALNUM_LOWER:
    case ID_PATTERN_TOKENS.ALNUM_LOWERCASE:
      // Alphanumerics (Lowercase) → [a-z0-9]
      if (displayLength === 1) return PLACEHOLDER_SAMPLES.ALNUM_LOWER_START;
      if (displayLength === 2) return PLACEHOLDER_SAMPLES.ALNUM_LOWER_START + '1';
      // For length >= 3, start with a letter followed by digits (a12 style)
      return PLACEHOLDER_SAMPLES.ALNUM_LOWER_START + PLACEHOLDER_SAMPLES.DIGITS.repeat(Math.ceil((displayLength - 1) / PLACEHOLDER_SAMPLES.DIGITS.length)).slice(0, displayLength - 1);

    case ID_PATTERN_TOKENS.ALPHANUMERIC_MIXED:
    case ID_PATTERN_TOKENS.ALNUM_MIXED:
      // Alphanumerics (Mixed Case) → [A-Za-z0-9]
      if (displayLength === 1) return PLACEHOLDER_SAMPLES.ALNUM_MIXED_START;
      if (displayLength === 2) return PLACEHOLDER_SAMPLES.ALNUM_MIXED_START + '1';
      // For length >= 3, start with a letter followed by digits (A12 style)
      return PLACEHOLDER_SAMPLES.ALNUM_MIXED_START + PLACEHOLDER_SAMPLES.DIGITS.repeat(Math.ceil((displayLength - 1) / PLACEHOLDER_SAMPLES.DIGITS.length)).slice(0, displayLength - 1);

    case ID_PATTERN_TOKENS.HEX_UPPERCASE:
    case ID_PATTERN_TOKENS.HEX:
      // Hex (Uppercase)
      return PLACEHOLDER_SAMPLES.HEX_UPPER.slice(0, displayLength);

    case ID_PATTERN_TOKENS.HEX_LOWERCASE:
      // Hex (Lowercase)
      return PLACEHOLDER_SAMPLES.HEX_LOWER.slice(0, displayLength);

    default:
      // Generic fallback: if rule contains common keywords, infer type
      if (normalizedRule.includes(ID_PATTERN_TOKENS.LETTER) || normalizedRule.includes(PATTERN_KEYWORDS.CHAR)) {
        return PLACEHOLDER_SAMPLES.GENERIC_LETTER.slice(0, displayLength);
      }
      if (normalizedRule.includes(ID_PATTERN_TOKENS.DIGIT) || normalizedRule.includes(PATTERN_KEYWORDS.NUM)) {
        return PLACEHOLDER_SAMPLES.GENERIC_DIGIT.slice(0, displayLength);
      }
      if (normalizedRule.includes(ID_PATTERN_TOKENS.ALNUM) || normalizedRule.includes(PATTERN_KEYWORDS.ALPHANUMERIC)) {
        return PLACEHOLDER_SAMPLES.GENERIC_ALNUM.slice(0, displayLength);
      }
      // Ultimate fallback: return generic placeholder
      return PLACEHOLDER_SAMPLES.GENERIC_CHAR.repeat(displayLength);
  }
}

/**
 * Parse a pattern string into structured segments.
 * Rules:
 * - {TAG:length} → editable segment (has colon)
 * - {TAG} → dynamic constant (no colon, looked up in constants)
 * - Text outside {} → static constant
 */
function parsePattern(pattern, constants, existingIds = []) {
  if (!pattern) return [];
  const regex = new RegExp('{([^}]+)}', REGEX_FLAGS.GLOBAL);
  let match;
  const parts = [];
  let lastIndex = 0;

  while ((match = regex.exec(pattern)) !== null) {
    // Add any text before this token as a fixed segment
    const before = pattern.slice(lastIndex, match.index);
    if (before) parts.push({ type: 'fixed', value: before });

    const tokenContent = match[1];
    
    // Check if token contains a colon (indicates editable segment)
    const hasColon = tokenContent.includes(':');
    
    if (hasColon) {
      // Editable segment: {TAG:length} or {INCR:startValue}
      const [rule, lengthStr] = tokenContent.split(':');
      const normalizedRule = rule.trim().toUpperCase();
      
      // For INCR, the value after colon is the starting value (e.g., 01, 001), not length
      // INCR segments should be auto-populated with the next incremental value and non-editable
      if (normalizedRule === ID_PATTERN_TOKENS.INCR || normalizedRule === ID_PATTERN_TOKENS.INCREMENT || normalizedRule === ID_PATTERN_TOKENS.INCREMENTAL) {
        // For INCR, treat as fixed segment with the next incremental value auto-populated
        // Calculate next value based on existing participant IDs
        const startValue = lengthStr || '01';
        // If existingIds is provided, calculate next value, otherwise use starting value
        const nextValue = (existingIds && existingIds.length > 0) 
          ? (getNextIncrValue(pattern, constants, existingIds) || startValue)
          : startValue;
        parts.push({
          type: 'fixed',
          value: nextValue,
          isIncr: true,
          incrStartValue: startValue,
          incrLength: startValue.length,
        });
      } else {
        // For other rules, lengthStr is the length
        const length = lengthStr ? Number(lengthStr) : undefined;
        const placeholderValue = length || 0;
        
        parts.push({
          type: 'editable',
          rule: rule.trim(),
          length: length,
          placeholder: getSamplePlaceholder(rule.trim(), placeholderValue),
          value: '',
        });
      }
    } else {
      // Dynamic constant: {TAG} (no colon)
      // Check if it's provided in constants map
      if (constants && constants[tokenContent] !== undefined) {
        parts.push({ type: 'fixed', value: constants[tokenContent] });
      } else {
        // Unknown constant - treat as fixed empty or the token name itself
        // This allows patterns to reference constants that may not be available yet
        parts.push({ type: 'fixed', value: `{${tokenContent}}` });
      }
    }
    lastIndex = regex.lastIndex;
  }

  // Add any text after the last token
  const after = pattern.slice(lastIndex);
  if (after) parts.push({ type: 'fixed', value: after });

  return parts;
}

/**
 * Parse an existing ID value back into pattern segments.
 * Extracts editable segment values by matching the pattern structure.
 */
function parseValueIntoSegments(value, segments, constants, sanitizeValue) {
  if (!value || !segments?.length) return segments;

  let cursor = 0;
  const updated = [...segments];

  const getNextFixed = (i) =>
    segments.slice(i + 1).find(s => s.type === "fixed");

  segments.forEach((seg, i) => {
    if (seg.type === "fixed") {
      // For INCR fixed segments, extract the value from the existing ID based on length
      // Don't try to match the value - extract it from the remaining string
      if (seg.isIncr && seg.incrLength) {
        // Check if this is the last segment or all remaining segments are INCR
        const isLastOrAllIncr = i === segments.length - 1 || 
          segments.slice(i + 1).every(s => s.type === 'fixed' && s.isIncr);
        
        let incrValue = null;
        
        if (isLastOrAllIncr) {
          // If INCR is at the end, extract the last N digits where N is incrLength
          const lastNChars = value.slice(-seg.incrLength);
          if (/^\d+$/.test(lastNChars) && lastNChars.length === seg.incrLength) {
            incrValue = lastNChars;
            cursor = value.length;
          }
        } else {
          // Extract from current cursor position
          const remainingLength = value.length - cursor;
          if (remainingLength >= seg.incrLength) {
            const extracted = value.substring(cursor, cursor + seg.incrLength);
            if (/^\d+$/.test(extracted) && extracted.length === seg.incrLength) {
              incrValue = extracted;
              cursor += seg.incrLength;
            }
          }
        }
        
        // Update the segment with the extracted value, or keep the starting value if extraction failed
        if (incrValue) {
          updated[i] = {
            ...seg,
            value: incrValue,
          };
        } else {
          // If extraction failed, advance cursor by incrLength to continue parsing
          cursor += seg.incrLength;
        }
      } else {
        // For regular fixed segments, match the value
        if (value.startsWith(seg.value, cursor)) {
          cursor += seg.value.length;
        } else {
          // If fixed segment doesn't match, try to find it in the remaining value
          // This handles cases where the value might have slight mismatches
          const foundIndex = value.indexOf(seg.value, cursor);
          if (foundIndex !== -1) {
            cursor = foundIndex + seg.value.length;
          }
        }
      }
      return;
    }

    // --------------------
    // Editable segment
    // --------------------

    const nextFixed = getNextFixed(i);

    const fixedLength = seg.length ?? null;

    const endPos =
      fixedLength !== null
        ? cursor + fixedLength
        : nextFixed
          ? value.indexOf(nextFixed.value, cursor)
          : value.length;

    const extracted =
      endPos >= cursor
        ? value.slice(cursor, fixedLength ? endPos : endPos)
        : value.slice(cursor);

    cursor = fixedLength
      ? endPos
      : nextFixed && endPos !== -1
        ? endPos
        : value.length;

    if (!extracted) return;

    const sanitized = sanitizeValue(seg.rule, extracted);

    updated[i] = {
      ...seg,
      value: fixedLength ? sanitized.slice(0, fixedLength) : sanitized
    };
  });

  return updated;
}

/**
 * Calculate the next INCR value based on existing participant IDs.
 * @param {string} format - The format pattern
 * @param {object} constants - Constants map (STUDY_ID, SITE_ID)
 * @param {Array<string>} existingIds - Array of existing participant subject IDs
 * @returns {string|null} - The next INCR value to use, or null if no INCR token found
 */
function getNextIncrValue(format, constants, existingIds = []) {
  if (!format || !existingIds || existingIds.length === 0) return null;

  const tokenRegex = /\{([^}]+)\}/g;
  let match;
  const incrTokens = [];
  let editableSegmentIndex = 0;

  // Find all INCR tokens and their positions
  while ((match = tokenRegex.exec(format)) !== null) {
    const tokenContent = match[1];
    const hasColon = tokenContent.includes(':');

    if (hasColon) {
      const [rule, valueStr] = tokenContent.split(':');
      const normalizedRule = rule.trim().toUpperCase();

      if (normalizedRule === ID_PATTERN_TOKENS.INCR || normalizedRule === ID_PATTERN_TOKENS.INCREMENT || normalizedRule === ID_PATTERN_TOKENS.INCREMENTAL) {
        const startValue = valueStr || '01';
        incrTokens.push({
          startValue,
          length: startValue.length,
          editableSegmentIndex,
        });
      }
      editableSegmentIndex++;
    }
  }

  if (incrTokens.length === 0) return null;

  // For now, use the first INCR token (we can extend this for multiple INCR tokens later)
  const incrToken = incrTokens[0];
  const maxValue = parseInt(incrToken.startValue, 10);

  // Build format with constants replaced for parsing
  let formatForParsing = format;
  if (constants && typeof constants === 'object') {
    formatForParsing = format.replace(/\{([^}:]+)\}/g, (match, constantName) => {
      if (!match.includes(':')) {
        const constantValue = constants[constantName];
        if (constantValue !== undefined && constantValue !== null) {
          return String(constantValue);
        }
      }
      return match;
    });
  }

  // Extract INCR values from existing IDs
  // Strategy: Use literal text segments as anchors to locate the INCR segment position
  // This handles IDs with different constant values (different STUDY_ID, SITE_ID, etc.)
  const incrValues = [];
  
  // Parse format into parts (tokens and literals)
  const tokenRegex2 = /\{([^}]+)\}/g;
  const formatParts = [];
  let lastIndex = 0;
  let match2;
  tokenRegex2.lastIndex = 0;
  
  while ((match2 = tokenRegex2.exec(format)) !== null) {
    const before = format.substring(lastIndex, match2.index);
    if (before) formatParts.push({ type: 'literal', value: before });
    formatParts.push({ type: 'token', value: match2[1] });
    lastIndex = match2.index + match2[0].length;
  }
  const after = format.substring(lastIndex);
  if (after) formatParts.push({ type: 'literal', value: after });

  // Find INCR token position and the literal text that comes before it
  let editableSegIdx = 0;
  let incrPartIndex = -1;
  let literalBeforeIncr = null;
  let literalAfterIncr = null;
  
  formatParts.forEach((part, idx) => {
    if (part.type === 'token' && part.value.includes(':')) {
      const [rule, valueStr] = part.value.split(':');
      const normalizedRule = rule.trim().toUpperCase();
      if (normalizedRule === ID_PATTERN_TOKENS.INCR || normalizedRule === ID_PATTERN_TOKENS.INCREMENT || normalizedRule === ID_PATTERN_TOKENS.INCREMENTAL) {
        if (editableSegIdx === incrToken.editableSegmentIndex) {
          incrPartIndex = idx;
          // Find literal text before INCR
          for (let i = idx - 1; i >= 0; i--) {
            if (formatParts[i].type === 'literal' && formatParts[i].value) {
              literalBeforeIncr = formatParts[i].value;
              break;
            }
          }
          // Find literal text after INCR
          for (let i = idx + 1; i < formatParts.length; i++) {
            if (formatParts[i].type === 'literal' && formatParts[i].value) {
              literalAfterIncr = formatParts[i].value;
              break;
            }
          }
        }
      }
      editableSegIdx++;
    }
  });

  if (incrPartIndex < 0) return null;

  // Extract INCR values from each existing ID
  existingIds.forEach(id => {
    const trimmedId = String(id).trim();
    if (!trimmedId || trimmedId.length < incrToken.length) return;

    let incrStartPos = -1;

    // Strategy 1: If there's literal text before INCR, use it as anchor
    if (literalBeforeIncr) {
      const literalIndex = trimmedId.lastIndexOf(literalBeforeIncr);
      if (literalIndex >= 0) {
        incrStartPos = literalIndex + literalBeforeIncr.length;
      }
    }
    
    // Strategy 2: If INCR is at the end (no literal after), extract from the end
    if (incrStartPos < 0 && !literalAfterIncr) {
      incrStartPos = trimmedId.length - incrToken.length;
    }
    
    // Strategy 3: If there's literal text after INCR, use it to find position from the end
    if (incrStartPos < 0 && literalAfterIncr) {
      const literalAfterIndex = trimmedId.lastIndexOf(literalAfterIncr);
      if (literalAfterIndex >= incrToken.length) {
        incrStartPos = literalAfterIndex - incrToken.length;
      }
    }

    // Extract INCR value if we found a valid position
    if (incrStartPos >= 0 && incrStartPos + incrToken.length <= trimmedId.length) {
      const incrValue = trimmedId.substring(incrStartPos, incrStartPos + incrToken.length);
      const numValue = parseInt(incrValue, 10);
      // Validate it's a number and matches the expected length
      if (!isNaN(numValue) && incrValue.length === incrToken.length && /^\d+$/.test(incrValue)) {
        incrValues.push(numValue);
      }
    }
  });

  // Find the maximum INCR value
  const maxIncrValue = incrValues.length > 0 ? Math.max(...incrValues, maxValue) : maxValue;

  // Calculate next value
  const nextValue = maxIncrValue + 1;

  // Format with leading zeros if needed
  const formattedNext = String(nextValue).padStart(incrToken.length, '0');

  return formattedNext;
}

export default function PatternInput({
  pattern,
  constants,
  onIdChange,
  label,
  disabled = false,
  value, // Existing value to initialize with (for edit mode)
  existingIds = [], // Array of existing subject IDs to calculate next INCR value
}) {
  const [result, setResult] = useState('');
  const [segments, setSegments] = useState([]);
  const onIdChangeRef = useRef(onIdChange);
  onIdChangeRef.current = onIdChange;

  if (!pattern) return null;

  /**
   * Sanitize input value based on rule type.
   * Supports known patterns and falls back to generic behavior for unknown tags.
   */
  const sanitizeValue = (rule, rawVal) => {
    if (!rule || !rawVal) return rawVal;
    
    const normalizedRule = String(rule).toUpperCase().trim();
    let val = rawVal;

    // Use switch for primary rule matching
    switch (normalizedRule) {
      case ID_PATTERN_TOKENS.LETTER:
      case ID_PATTERN_TOKENS.LETTERS:
      case ID_PATTERN_TOKENS.LETTERS_UPPERCASE:
        // Letters (Uppercase) → only A-Z
        val = val.replace(new RegExp(`[^${CHAR_CLASSES.LETTERS}]`, REGEX_FLAGS.GLOBAL), '').toUpperCase();
        break;

      case ID_PATTERN_TOKENS.LETTERS_LOWERCASE:
      case ID_PATTERN_TOKENS.LETTER_LOWER:
        // Letters (Lowercase) → only a-z
        val = val.replace(new RegExp(`[^${CHAR_CLASSES.LETTERS}]`, REGEX_FLAGS.GLOBAL), '').toLowerCase();
        break;

      case ID_PATTERN_TOKENS.LETTERS_MIXED:
      case ID_PATTERN_TOKENS.LETTER_MIXED:
        // Letters (Mixed Case) → allow any case
        val = val.replace(new RegExp(`[^${CHAR_CLASSES.LETTERS}]`, REGEX_FLAGS.GLOBAL), '');
        break;

      case ID_PATTERN_TOKENS.DIGITS:
      case ID_PATTERN_TOKENS.DIGIT:
        // Digits (0-9)
        val = val.replace(new RegExp(`[^${CHAR_CLASSES.DIGITS}]`, REGEX_FLAGS.GLOBAL), '');
        break;

      case ID_PATTERN_TOKENS.ALPHANUMERIC_UPPERCASE:
      case ID_PATTERN_TOKENS.ALNUM:
      case ID_PATTERN_TOKENS.ALNUM_UPPERCASE:
        // Alphanumerics (Uppercase) → [A-Z0-9]
        val = val.replace(new RegExp(`[^${CHAR_CLASSES.ALNUM}]`, REGEX_FLAGS.GLOBAL), '').toUpperCase();
        break;

      case ID_PATTERN_TOKENS.ALPHANUMERIC_LOWERCASE:
      case ID_PATTERN_TOKENS.ALNUM_LOWER:
      case ID_PATTERN_TOKENS.ALNUM_LOWERCASE:
        // Alphanumerics (Lowercase) → [a-z0-9]
        val = val.replace(new RegExp(`[^${CHAR_CLASSES.ALNUM}]`, REGEX_FLAGS.GLOBAL), '').toLowerCase();
        break;

      case ID_PATTERN_TOKENS.ALPHANUMERIC_MIXED:
      case ID_PATTERN_TOKENS.ALNUM_MIXED:
        // Alphanumerics (Mixed Case) → [A-Za-z0-9]
        val = val.replace(new RegExp(`[^${CHAR_CLASSES.ALNUM}]`, REGEX_FLAGS.GLOBAL), '');
        break;

      case ID_PATTERN_TOKENS.INCR:
      case ID_PATTERN_TOKENS.INCREMENT:
      case ID_PATTERN_TOKENS.INCREMENTAL:
        // Incremental digits
        val = val.replace(new RegExp(`[^${CHAR_CLASSES.DIGITS}]`, REGEX_FLAGS.GLOBAL), '');
        break;

      case ID_PATTERN_TOKENS.HEX_UPPERCASE:
      case ID_PATTERN_TOKENS.HEX:
        // Hex (Uppercase)
        val = val.replace(new RegExp(`[^${CHAR_CLASSES.HEX_MIXED}]`, REGEX_FLAGS.GLOBAL), '').toUpperCase();
        break;

      case ID_PATTERN_TOKENS.HEX_LOWERCASE:
        // Hex (Lowercase)
        val = val.replace(new RegExp(`[^${CHAR_CLASSES.HEX_MIXED}]`, REGEX_FLAGS.GLOBAL), '').toLowerCase();
        break;

      default:
        // Generic fallback based on rule name keywords
        if (normalizedRule.includes(ID_PATTERN_TOKENS.LETTER) || normalizedRule.includes(PATTERN_KEYWORDS.CHAR)) {
          // Allow letters, case-insensitive
          val = val.replace(new RegExp(`[^${CHAR_CLASSES.LETTERS}]`, REGEX_FLAGS.GLOBAL), '');
        } else if (normalizedRule.includes(ID_PATTERN_TOKENS.DIGIT) || normalizedRule.includes(PATTERN_KEYWORDS.NUM)) {
          // Allow digits only
          val = val.replace(new RegExp(`[^${CHAR_CLASSES.DIGITS}]`, REGEX_FLAGS.GLOBAL), '');
        } else if (normalizedRule.includes(ID_PATTERN_TOKENS.ALNUM) || normalizedRule.includes(PATTERN_KEYWORDS.ALPHANUMERIC)) {
          // Allow alphanumeric
          val = val.replace(new RegExp(`[^${CHAR_CLASSES.ALNUM}]`, REGEX_FLAGS.GLOBAL), '');
        } else {
          // Ultimate fallback: allow alphanumeric and common separators
          val = val.replace(new RegExp(`[^${CHAR_CLASSES.ALNUM_WITH_SEPARATORS}]`, REGEX_FLAGS.GLOBAL), '');
        }
        break;
    }

    return val;
  };

  // Re-parse on pattern OR constants update
  // Preserve user input in editable segments when possible
  useEffect(() => {
    if (pattern) {
      const newSegments = parsePattern(pattern, constants, existingIds);
      
      // Try to preserve existing editable segment values if pattern structure hasn't changed
      setSegments(prevSegments => {
        // Priority 1: If value prop is provided, parse it to ensure correct initialization
        // This is especially important when editing existing participants
        if (value !== undefined && value !== null) {
          // Always parse the value when segments are empty (initial mount)
          if (prevSegments.length === 0) {
            if (value === '') {
              return newSegments;
            }
            return parseValueIntoSegments(value, newSegments, constants, sanitizeValue);
          }
          
          // Calculate current result from previous segments to compare with value prop
          // Build the full result including both fixed and editable segments
          const currentResult = prevSegments.map(s => s.value || '').join('');
          
          // If value prop is different from current result, parse it
          // This handles cases where the value prop changes (e.g., switching participants)
          // Always re-parse when value prop is provided to ensure correctness, especially for INCR segments
          if (value !== currentResult) {
            if (value === '') {
              // Clear all editable segments if value is empty
              return newSegments.map(seg => 
                seg.type === 'editable' ? { ...seg, value: '' } : seg
              );
            }
            // Re-parse the value to ensure all segments (especially INCR) are correctly populated
            return parseValueIntoSegments(value, newSegments, constants, sanitizeValue);
          }
          
          // If values match, we still need to ensure segment structure is updated
          // Map old editable values by their rule and length (to match similar segments)
          const editableValueMap = new Map();
          prevSegments.forEach((seg) => {
            if (seg.type === 'editable' && seg.value) {
              const key = `${seg.rule}:${seg.length || 'any'}`;
              // Keep the first value we encounter for this key
              if (!editableValueMap.has(key)) {
                editableValueMap.set(key, seg.value);
              }
            }
          });
          
          // Try to restore values to matching editable segments in the new pattern
          const restoredSegments = newSegments.map(seg => {
            if (seg.type === 'editable') {
              const key = `${seg.rule}:${seg.length || 'any'}`;
              const preservedValue = editableValueMap.get(key);
              if (preservedValue) {
                // Re-apply sanitization to preserved value
                const sanitized = sanitizeValue(seg.rule, preservedValue);
                return { ...seg, value: seg.length ? sanitized.slice(0, seg.length) : sanitized };
              }
            }
            return seg;
          });
          
          // Double-check: if the restored segments don't match the value prop, re-parse
          const restoredResult = restoredSegments.map(s => s.value || '').join('');
          if (value !== restoredResult) {
            return parseValueIntoSegments(value, newSegments, constants, sanitizeValue);
          }
          
          return restoredSegments;
        }
        
        // Priority 2: If no value prop or value hasn't changed, preserve existing values
        if (prevSegments.length === 0) {
          return newSegments;
        }
        
        // Map old editable values by their rule and length (to match similar segments)
        const editableValueMap = new Map();
        prevSegments.forEach((seg) => {
          if (seg.type === 'editable' && seg.value) {
            const key = `${seg.rule}:${seg.length || 'any'}`;
            // Keep the first value we encounter for this key
            if (!editableValueMap.has(key)) {
              editableValueMap.set(key, seg.value);
            }
          }
        });
        
        // Try to restore values to matching editable segments in the new pattern
        return newSegments.map(seg => {
          if (seg.type === 'editable') {
            const key = `${seg.rule}:${seg.length || 'any'}`;
            const preservedValue = editableValueMap.get(key);
            if (preservedValue) {
              // Re-apply sanitization to preserved value
              const sanitized = sanitizeValue(seg.rule, preservedValue);
              return { ...seg, value: seg.length ? sanitized.slice(0, seg.length) : sanitized };
            }
          }
          return seg;
        });
      });
    } else {
      setSegments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pattern, JSON.stringify(constants), value]);

  const updateSegment = (index, rawVal) => {
    if (disabled) return;

    const seg = segments[index];
    if (!seg || seg.type !== 'editable') return;

    // Sanitize the value based on the rule
    let val = sanitizeValue(seg.rule, rawVal);

    // Enforce length safely
    if (seg.length) val = val.slice(0, seg.length);

    // Update segments
    const updated = segments.map((s, idx) =>
      idx === index ? { ...s, value: val } : s
    );

    setSegments(updated);
  };

  // Fire callback on segment changes (use ref for onIdChange to avoid infinite loop when parent passes unstable callback)
  useEffect(() => {
    const editableSegments = segments.filter(s => s.type === 'editable')
    const allEditableEmpty = editableSegments.length > 0 && editableSegments.every(s => !s.value)

    const newResult = allEditableEmpty ? '' : segments.map(s => s.value).join('')

    if (result !== newResult) {
      setResult(newResult)
      onIdChangeRef.current(newResult)
    }
  }, [segments, result]);


  return (
    <div >
      <div className="label-small">{label}</div>

      {/* MAIN BOX */}
      <div
        style={{
          background: disabled ? '#f0f4f7' : '#e7f8ff',
          borderRadius: '8px',
          padding: '10px 14px',
          minHeight: '40px',
          display: 'flex',
          alignItems: 'center',
          columnGap: '4px',
          flexWrap: 'nowrap', // keep all segments on a single line
          overflow: 'hidden', // prevent error text from pushing layout
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      >
        {segments.map((seg, i) =>
          seg.type === 'fixed' ? (
            <span
              key={i}
              style={{
                color: '#555',
                fontSize: '16px',
                fontWeight: 500,
                userSelect: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {seg.value}
            </span>
          ) : (
            <input
              key={i}
              value={seg.value}
              placeholder={seg.placeholder}
              onChange={(e) => updateSegment(i, e.target.value)}
              disabled={disabled}
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: '16px',
                color: '#000',
                // Use fixed width based on pattern segment length so it stays
                // the same before and after site selection.
                minWidth: seg.length ? `${seg.length * 10}px` : '40px',
                flexShrink: 0,
                cursor: disabled ? 'not-allowed' : 'text',
              }}
            />
          )
        )}
      </div>
    </div>
  );
}
